/**
 * KAN-561: remembers whether the learner collapsed the course-navigation
 * sidebar, for the rest of the browser session.
 *
 * Pure helpers over a Storage-like object so the component can hand in
 * `window.sessionStorage` and tests can hand in a fake. Storage may be absent
 * (SSR, sandboxed iframes) or throw (private mode, quota) - both degrade to the
 * default and never throw, because a missing preference must not break the
 * player.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem?(key: string): void;
}

export const TIMELINE_COLLAPSED_KEY = 'coursePlayer.timelineCollapsed';

const DEFAULT_COLLAPSED = false;

export function readTimelineCollapsed(storage: StorageLike | null | undefined): boolean {
  if (!storage) return DEFAULT_COLLAPSED;
  try {
    const raw = storage.getItem(TIMELINE_COLLAPSED_KEY);
    if (raw === null || raw === undefined) return DEFAULT_COLLAPSED;
    return raw === '1';
  } catch {
    return DEFAULT_COLLAPSED;
  }
}

export function writeTimelineCollapsed(storage: StorageLike | null | undefined, collapsed: boolean): void {
  if (!storage) return;
  try {
    storage.setItem(TIMELINE_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // Storage unavailable (private mode / quota) - the preference is a
    // convenience, not a requirement.
  }
}

/** The browser's sessionStorage, or null where accessing it throws. */
export function safeSessionStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch {
    return null;
  }
}
